/**
 * Модель раздела «Синхронизация» (ADR-23). Договор — `src/Sync/Sync.Contracts` (RP-SYN1): служба `sync-api` только находит
 * расхождения и по команде человека применяет выбранное; клиент ничего не решает сам. Wire-форма проверяется на границе
 * (`api/contracts.ts`).
 */

/** Вид данных расхождения (`SyncDataKinds`): номенклатура (запись и поля ядра), штрихкоды, цены, фото. */
export const DATA_KINDS = ['item', 'barcode', 'price', 'photo'] as const
export type DataKind = (typeof DATA_KINDS)[number]

/** Пометка строки (`SyncDivergenceTypes`): добавлено, изменено, удалено, есть только в общем каталоге. */
export const CHANGE_TYPES = ['added', 'changed', 'removed', 'only-common'] as const
export type ChangeType = (typeof CHANGE_TYPES)[number]

/** Чего касается строка — выводится из вида данных и поля: позиция целиком, поле ядра, штрихкод, вид цены, фото. */
export type Subject = 'item' | 'field' | 'barcode' | 'price' | 'photo'

/** Повод проверки (`SyncCheckTriggers`): событие источника, «Проверить сейчас», ночная сверка. */
export const CHECK_TRIGGERS = ['event', 'manual', 'nightly'] as const
export type CheckTrigger = (typeof CHECK_TRIGGERS)[number]

export const CHECK_STATES = ['running', 'completed', 'failed'] as const
export type CheckState = (typeof CHECK_STATES)[number]

/** Что сделать с выбранными строками (`SyncApplyActions`): перенести значение, удалить в целях, вернуть удалённую запись. */
export const APPLY_ACTIONS = ['copy', 'delete', 'restore'] as const
export type ApplyAction = (typeof APPLY_ACTIONS)[number]

/**
 * Что делает строка задачи в целевом источнике (`SyncApplyItemActions`): создать, изменить поле, удалить, вернуть, штрихкод, фото, цену; `none` — строка
 * не применялась (действие к ней не подошло, причина — в тексте строки).
 */
export const JOB_ITEM_ACTIONS = ['create', 'update', 'archive', 'restore', 'barcode-add', 'barcode-remove', 'photo-add', 'photo-remove', 'price-set', 'none'] as const
export type JobItemAction = (typeof JOB_ITEM_ACTIONS)[number]

export const JOB_STATES = ['queued', 'running', 'completed', 'failed'] as const
export type JobState = (typeof JOB_STATES)[number]

export const JOB_ITEM_STATES = ['pending', 'running', 'succeeded', 'failed'] as const
export type JobItemState = (typeof JOB_ITEM_STATES)[number]

/** Уровень «не отслеживать» (`SyncIgnoreLevels`): запись источника, общий товар, весь источник — по одному виду данных. */
export const IGNORE_LEVELS = ['record', 'product', 'source'] as const
export type IgnoreLevel = (typeof IGNORE_LEVELS)[number]

/** Чем совпал кандидат инициализации (`SyncInitMatches`). */
export const MATCH_KINDS = ['name', 'barcode', 'both'] as const
export type MatchKind = (typeof MATCH_KINDS)[number]

/** Правило цены у связи товара для пары источников (`SyncPriceVatRules`): «+НДС» — второй источник пары с НДС. */
export const PRICE_VAT_RULES = ['same', 'plus-vat'] as const
export type PriceVatRule = (typeof PRICE_VAT_RULES)[number]

/**
 * Как перенести цену в окне применения — направление называет человек: «добавить НДС» — цель с НДС (пара образец → цель, «+НДС»),
 * «вычесть НДС» — образец с НДС (пара цель → образец, «+НДС»). Ставку берёт служба у товара в источнике с НДС; её же строка цены показывает.
 */
export const PRICE_TRANSFERS = ['same', 'add-vat', 'remove-vat'] as const
export type PriceTransfer = (typeof PRICE_TRANSFERS)[number]

/** Типы полей шаблона создания (`Resource.Contracts.SyncCreateFieldTypes`). */
export const FIELD_TYPES = ['choice', 'text', 'boolean', 'integer'] as const
export type FieldType = (typeof FIELD_TYPES)[number]

export interface CheckRun {
  id: string
  resourceId: string
  trigger: CheckTrigger
  state: CheckState
  read: number
  /** `null` — источник ещё не назвал размер. */
  total: number | null
  errorCode: string | null
  /** Текст отказа дословно (ADR-8). */
  errorMessage: string | null
  startedAt: string
  finishedAt: string | null
}

export interface SourceTile {
  resourceId: string
  kind: string
  displayName: string
  itemCount: number
  barcodeCount: number
  divergenceCount: number
  lastCheck: CheckRun | null
}

export interface CommonCatalogTile {
  productCount: number
  /** Общие товары без единой связи — «есть только в общем каталоге». */
  onlyCommonCount: number
  /** Под каким идентификатором общий каталог стоит стороной в строках фото (своё S3): значение, цель и образец, «не отслеживать». */
  resourceId: string
}

export interface SourcesSummary {
  commonCatalog: CommonCatalogTile
  sources: SourceTile[]
}

/**
 * Значение строки в одном источнике; `present: false` — записи (штрихкода, фото) там нет. Цена — число строкой в инвариантной культуре; у цены
 * ещё вид цены этого источника (ключ и имя) и ставка НДС товара в нём в процентах (`null` — без НДС или ставка не прочитана).
 */
export interface SourceValue {
  resourceId: string
  remoteKey: string | null
  present: boolean
  value: string | null
  priceTypeKey: string | null
  priceTypeName: string | null
  vatPercent: number | null
}

export interface Divergence {
  id: number
  type: ChangeType
  dataKind: DataKind
  productId: string | null
  /** Источник, в котором позиция появилась или изменилась. */
  originResourceId: string | null
  /** Поле ядра у различия полей. */
  field: string | null
  /** Штрихкод, вид цены или отпечаток фото у строки набора. */
  subject: string | null
  /** Наименование позиции дословно. */
  title: string
  values: SourceValue[]
  detectedAt: string
}

export interface DivergencesPage {
  items: Divergence[]
  total: number
  page: number
  pageSize: number
}

export interface DivergenceCount {
  total: number
}

export interface TemplateOption {
  value: string
  label: string
}

/** Поле, которое целевой источник требует при создании (`Resource.Contracts.SyncCreateField`, план 3.7). */
export interface TemplateField {
  key: string
  label: string
  type: FieldType
  required: boolean
  options: TemplateOption[]
  /** Значение из настроек «создавать по умолчанию» источника; `null` — не задано. */
  defaultValue: string | null
}

export interface CreateTemplate {
  resourceId: string
  fields: TemplateField[]
}

/** Значения создания одного целевого источника: для всех строк и поверх них — для отдельных строк (ключ — номер строки). */
export interface CreateOptions {
  resourceId: string
  defaults: Readonly<Record<string, string>>
  rows: Readonly<Record<string, Readonly<Record<string, string>>>>
}

export interface ApplyCommand {
  operationId: string
  action: ApplyAction
  divergenceIds: number[]
  targets: string[]
  sourceResourceId: string | null
  createOptions: CreateOptions[] | null
}

export interface JobItem {
  id: number
  divergenceId: number | null
  dataKind: DataKind
  action: JobItemAction
  sourceResourceId: string | null
  targetResourceId: string
  remoteKey: string | null
  /** Что и куда — словами службы. */
  title: string
  state: JobItemState
  errorCode: string | null
  /** Ответ источника дословно. */
  message: string | null
  requestId: string | null
  finishedAt: string | null
}

export interface ApplyJob {
  id: string
  state: JobState
  createdBy: string
  createdByName: string | null
  total: number
  done: number
  failed: number
  errorMessage: string | null
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  items: JobItem[]
}

export interface Ignore {
  id: number
  level: IgnoreLevel
  resourceId: string
  dataKind: DataKind
  remoteKey: string | null
  productId: string | null
  subject: string | null
  createdBy: string
  createdAt: string
}

export interface IgnoreCommand {
  operationId: string
  level: IgnoreLevel
  resourceId: string
  dataKind: DataKind
  remoteKey: string | null
  productId: string | null
  subject: string | null
}

export interface InitCandidate {
  productId: string
  productName: string
  remoteKey: string
  name: string
  matchedBy: MatchKind
}

export interface InitAmbiguous {
  matchedBy: MatchKind
  value: string
  remoteKeys: string[]
  productIds: string[]
}

export interface InitProposal {
  resourceId: string
  candidates: InitCandidate[]
  ambiguous: InitAmbiguous[]
}

export interface InitFailure {
  productId: string
  remoteKey: string
  message: string
}

export interface InitResult {
  created: number
  alreadyLinked: number
  failed: InitFailure[]
}

export interface PriceTypeMap {
  firstResourceId: string
  firstPriceTypeKey: string
  secondResourceId: string
  secondPriceTypeKey: string
}

/** Вид цены источника из его ленты цен: ключ и имя дословно (`null` — источник имени не назвал). */
export interface PriceType {
  key: string
  name: string | null
}

/** Виды цен одного источника; пусто — лента цен источника ещё не прочитана. */
export interface PriceTypeSource {
  resourceId: string
  priceTypes: PriceType[]
}

/** Окно сопоставления: сохранённые пары, виды цен источников и предложения — виды с одинаковым именем, ещё не сопоставленные. */
export interface PriceMapsView {
  items: PriceTypeMap[]
  sources: PriceTypeSource[]
  suggestions: PriceTypeMap[]
}

export interface PriceVatRuleItem {
  productId: string
  firstResourceId: string
  secondResourceId: string
  rule: PriceVatRule
}

export interface Schedule {
  /** Время ночной сверки «ЧЧ:ММ». */
  nightlyAt: string
  timeZone: string
}

export interface JournalEntry {
  jobId: string
  itemId: number
  actor: string
  actorName: string | null
  at: string
  dataKind: DataKind
  action: JobItemAction
  sourceResourceId: string | null
  targetResourceId: string
  title: string
  state: JobItemState
  message: string | null
}

export interface JournalPage {
  items: JournalEntry[]
  total: number
  page: number
  pageSize: number
}

/** Окно расхождений общего каталога: позиции без единой связи (`only-common`), без источника в запросе. */
export const CATALOG_SOURCE = 'catalog'

/** Страница журнала. */
export const PAGE_SIZE = 100

/** Страница расхождений — предел договора (`SyncLimits.MaxPageSize`): «выбрать все» охватывает массовый случай владельца одной страницей. */
export const DIVERGENCE_PAGE_SIZE = 500

/** Пока идёт проверка или задача, сводка и ход перечитываются раз в 2 с. */
export const ACTIVE_POLL_MS = 2_000

/** Число у пункта меню обновляется само, без F5: расхождения появляются, пока раздел закрыт. */
export const COUNT_POLL_MS = 60_000

/** Ошибка для экрана: заголовок, дословный текст, код и запрос, текст для «Копировать». */
export interface ShownError {
  title: string
  message: string
  code: string
  requestId: string | null
  report: string
}
